/**
 * The visualizer map.
 *
 * Layout direction
 * ----------------
 * The API hands back edges as parent(kanji) -> child(component). For teaching, the
 * useful reading runs the other way: start from a simple part on the left and follow
 * it out to every kanji built from it. So we invert the edges for layout, giving
 * columns of increasing complexity left to right:
 *
 *     一 ──> 十 ──> 土 ──> 王 ──> 玉 ──> 国
 *
 * Tree from a DAG
 * ---------------
 * A kanji has several components, so the display graph is a DAG, not a tree, and a
 * naive tidy-tree would draw the same character many times. Each node therefore gets
 * one *primary* parent (its lowest-column component, tie-broken by family size) which
 * defines the tree skeleton; the remaining relationships are drawn as faint secondary
 * links. Every character appears exactly once.
 *
 * Rendering
 * ---------
 * Plain SVG inside one transformed <g>, so pan and zoom never re-layout. Branches are
 * collapsed by default and opened by the red disclosure arrow, which keeps the node
 * count on screen in the low hundreds even for N1's ~1800 characters.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { levelColor, type Graph, type KanjiNode, type Level, type Status } from '../api'

// -------------------------------------------------------------------- geometry
const NODE_H = 30
const ROW_GAP = 10
const COL_W = 148
const PAD = 60
const MIN_SCALE = 0.15
const MAX_SCALE = 3
/** Opening zoom: 1:1, where the kanji glyphs are comfortably readable. */
const DEFAULT_SCALE = 1
const TOP_MARGIN_X = 16
const TOP_MARGIN_Y = 20

/** Node pill width grows with the badges it has to carry. */
function nodeWidth(n: KanjiNode): number {
  let w = 40 // glyph + padding
  if (n.level) w += 30
  w += 24 // role badge
  if (n.status === 'mastered') w += 20
  return w
}

interface Placed {
  node: KanjiNode
  x: number
  y: number
  w: number
  col: number
  kids: string[]        // primary children, in draw order
  extraKids: string[]   // secondary (non-primary) relationships
  hasKids: boolean
  /** The database knows of more neighbours than the graph currently holds - either
   *  more kanji that use this one, or components of it we have not fetched. Lets a
   *  node that arrived with nothing attached still offer a disclosure arrow. */
  moreAvailable: boolean
}

export interface Layout {
  placed: Map<string, Placed>
  visible: Placed[]
  links: { from: Placed; to: Placed; primary: boolean }[]
  width: number
  height: number
}

/**
 * Assign a column to every node: 0 for characters with no components inside this
 * subgraph, otherwise one past the deepest component. Iterative relaxation, so a
 * cycle in the data cannot hang the layout.
 */
function assignColumns(nodes: KanjiNode[], childrenOf: Map<string, string[]>): Map<string, number> {
  const col = new Map<string, number>()
  for (const n of nodes) col.set(n.char, 0)
  for (let pass = 0; pass < 12; pass++) {
    let moved = false
    for (const n of nodes) {
      const parts = childrenOf.get(n.char)
      if (!parts?.length) continue
      const deepest = Math.max(...parts.map((p) => col.get(p) ?? 0))
      if (deepest + 1 > (col.get(n.char) ?? 0)) {
        col.set(n.char, deepest + 1)
        moved = true
      }
    }
    if (!moved) break
  }
  return col
}

export function buildLayout(
  graph: Graph,
  collapsed: Set<string>,
  /** Overlay nodes are pinned under the character they were revealed from. Without
   *  this, fetching a node's remaining components can hand it a "better" primary
   *  parent and make it jump to a different branch the moment you open it. */
  pinned?: ReadonlyMap<string, string>,
  /** Characters whose neighbours have already been fetched. Needed because the
   *  stored child_count includes single-stroke parts that the graph endpoints filter
   *  out, so a count comparison alone would keep claiming there is more to load. */
  explored?: ReadonlySet<string>,
): Layout {
  const byChar = new Map(graph.nodes.map((n) => [n.char, n]))

  // components of a kanji (API direction)
  const childrenOf = new Map<string, string[]>()
  for (const e of graph.edges) {
    if (!byChar.has(e.source) || !byChar.has(e.target)) continue
    const list = childrenOf.get(e.source) ?? []
    list.push(e.target)
    childrenOf.set(e.source, list)
  }

  const col = assignColumns(graph.nodes, childrenOf)

  // Inverted direction: component -> kanji that uses it.
  const usedBy = new Map<string, string[]>()
  for (const [kanji, parts] of childrenOf) {
    for (const p of parts) {
      const list = usedBy.get(p) ?? []
      list.push(kanji)
      usedBy.set(p, list)
    }
  }

  // Primary parent = the component sitting in the lowest column (so the chain reads
  // simple -> complex); ties go to the component with the bigger family, which keeps
  // the well-known radicals as trunks rather than leaves.
  const primaryParent = new Map<string, string>()
  for (const n of graph.nodes) {
    const parts = childrenOf.get(n.char)
    if (!parts?.length) continue
    const pin = pinned?.get(n.char)
    if (pin && byChar.has(pin) && parts.includes(pin)) {
      primaryParent.set(n.char, pin)
      continue
    }
    let best = parts[0]
    for (const p of parts.slice(1)) {
      const a = col.get(p) ?? 0
      const b = col.get(best) ?? 0
      if (a < b || (a === b && (usedBy.get(p)?.length ?? 0) > (usedBy.get(best)?.length ?? 0))) {
        best = p
      }
    }
    primaryParent.set(n.char, best)
  }

  const primaryKids = new Map<string, string[]>()
  for (const [kanji, parent] of primaryParent) {
    const list = primaryKids.get(parent) ?? []
    list.push(kanji)
    primaryKids.set(parent, list)
  }

  /**
   * Order siblings by properties intrinsic to the character - never by what the
   * graph happens to hold.
   *
   * Sorting by the in-graph family size meant a node's rank changed the moment more
   * nodes were merged, so selecting a character visibly reordered its siblings and
   * shunted it up the column. parent_count comes from the database and does not move.
   */
  const sortKids = (chars: string[]) =>
    chars.sort((a, b) => {
      const x = byChar.get(a)
      const y = byChar.get(b)
      return (
        (y?.parent_count ?? 0) - (x?.parent_count ?? 0) ||
        (x?.strokes ?? 99) - (y?.strokes ?? 99) ||
        a.localeCompare(b)
      )
    })

  const placed = new Map<string, Placed>()
  for (const n of graph.nodes) {
    const kids = sortKids([...(primaryKids.get(n.char) ?? [])])
    const extraKids = (usedBy.get(n.char) ?? []).filter((k) => primaryParent.get(k) !== n.char)
    placed.set(n.char, {
      node: n,
      x: 0,
      y: 0,
      w: nodeWidth(n),
      col: col.get(n.char) ?? 0,
      kids,
      extraKids,
      hasKids: kids.length > 0,
      // Only one direction can be filled in by opening a node: the kanji built from
      // it. A character nothing else uses has an empty family and gets no arrow,
      // rather than one that reveals nothing.
      moreAvailable:
        !explored?.has(n.char) && n.parent_count > kids.length + extraKids.length,
    })
  }

  // Roots of the display tree: nothing in the subgraph uses them as a component.
  const roots = graph.nodes
    .filter((n) => !primaryParent.has(n.char))
    .map((n) => n.char)
  sortKids(roots)

  // Tidy-tree walk: y is allocated by a running cursor, and a parent centres itself
  // over its visible children.
  let cursor = PAD
  const visible: Placed[] = []

  const walk = (char: string, depth: number): number => {
    const p = placed.get(char)
    if (!p) return cursor
    p.x = PAD + depth * COL_W
    visible.push(p)

    const open = p.hasKids && !collapsed.has(char)
    if (!open) {
      p.y = cursor
      cursor += NODE_H + ROW_GAP
      return p.y
    }
    const ys: number[] = []
    for (const kid of p.kids) ys.push(walk(kid, depth + 1))
    p.y = ys.length ? (ys[0] + ys[ys.length - 1]) / 2 : cursor
    return p.y
  }

  for (const r of roots) walk(r, 0)

  const shown = new Set(visible.map((p) => p.node.char))
  const links: Layout['links'] = []
  for (const p of visible) {
    if (collapsed.has(p.node.char)) continue
    for (const kid of p.kids) {
      const q = placed.get(kid)
      if (q && shown.has(kid)) links.push({ from: p, to: q, primary: true })
    }
    for (const kid of p.extraKids) {
      const q = placed.get(kid)
      // Only draw a secondary link if both ends happen to be on screen already.
      if (q && shown.has(kid)) links.push({ from: p, to: q, primary: false })
    }
  }

  const width = Math.max(...visible.map((p) => p.x + p.w), 400) + PAD
  const height = Math.max(cursor, 300) + PAD
  return { placed, visible, links, width, height }
}

// ------------------------------------------------------------------- component
interface Props {
  graph: Graph
  selected: string | null
  focus: string | null
  /** Identity of the underlying *filter* (level/branch/depth). Collapse state and
   *  the opening viewport reset when this changes - but not when nodes are merged
   *  into the graph, which would otherwise throw away the learner's position every
   *  time they selected a character. */
  viewKey: string
  /** Characters that came from outside the level filter. Drawn greyed out and
   *  dashed, but still fully interactive. */
  dimmed?: ReadonlySet<string>
  /** Chars whose neighbours are currently being fetched, for the arrow's busy state. */
  loadingFamily?: ReadonlySet<string>
  /** Pins overlay nodes under the character they were revealed from. */
  pinned?: ReadonlyMap<string, string>
  /** Characters already fetched, so their arrows stop offering more to load. */
  explored?: ReadonlySet<string>
  /** Asked to load `char`'s missing neighbours - the kanji that use it and its own
   *  components - when the graph does not hold them yet. */
  onExpandRequest?: (char: string) => void
  onSelect: (char: string) => void
  onHover?: (node: KanjiNode | null) => void
}

interface View {
  x: number
  y: number
  k: number
}

const NO_DIM: ReadonlySet<string> = new Set()

export default function KanjiMap({
  graph,
  selected,
  focus,
  viewKey,
  dimmed = NO_DIM,
  loadingFamily = NO_DIM,
  pinned,
  explored,
  onExpandRequest,
  onSelect,
  onHover,
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null)
  const [view, setView] = useState<View>({ x: TOP_MARGIN_X, y: TOP_MARGIN_Y, k: DEFAULT_SCALE })
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [hovered, setHovered] = useState<string | null>(null)
  const [size, setSize] = useState({ w: 800, h: 600 })

  // Collapse everything that has children when the filter changes, so the first
  // paint is a readable set of trunks rather than a wall of nodes.
  const lastKey = useRef<string>('')
  useEffect(() => {
    if (lastKey.current === viewKey) return
    lastKey.current = viewKey
    setCollapsed(new Set(graph.edges.map((e) => e.target)))
  }, [viewKey, graph.edges])

  // Open the selected character once, when it becomes selected - selecting it is the
  // gesture that asks "show me what this is used in".
  //
  // This has to be a one-shot effect, not a derived override. Deriving it meant the
  // node could never be closed again: the toggle set `collapsed`, and the override
  // removed it on the very next render, so its disclosure arrow did nothing.
  useEffect(() => {
    if (!selected) return
    setCollapsed((prev) => {
      if (!prev.has(selected)) return prev
      const next = new Set(prev)
      next.delete(selected)
      return next
    })
  }, [selected])

  const layout = useMemo(
    () => buildLayout(graph, collapsed, pinned, explored),
    [graph, collapsed, pinned, explored],
  )

  useLayoutEffect(() => {
    const el = hostRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setSize({ w: Math.max(320, width), h: Math.max(240, height) })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const toggle = useCallback(
    (char: string) => {
      const p = layout.placed.get(char)
      const isOpen = Boolean(p?.hasKids) && !collapsed.has(char)
      // Opening a node whose family is only partly loaded asks the page for the rest.
      if (!isOpen && p?.moreAvailable) onExpandRequest?.(char)
      setCollapsed((prev) => {
        const next = new Set(prev)
        if (next.has(char)) next.delete(char)
        else next.add(char)
        return next
      })
    },
    [collapsed, layout.placed, onExpandRequest],
  )

  /** Open every ancestor of `char` and centre the viewport on it. */
  const revealAndCentre = useCallback(
    (char: string) => {
      const parentOf = new Map<string, string>()
      for (const [c, p] of layout.placed) {
        for (const kid of p.kids) parentOf.set(kid, c)
      }
      const chain: string[] = []
      let cur: string | undefined = parentOf.get(char)
      while (cur) {
        chain.push(cur)
        cur = parentOf.get(cur)
      }
      if (chain.length) {
        setCollapsed((prev) => {
          const next = new Set(prev)
          for (const c of chain) next.delete(c)
          return next
        })
      }
      // Position after the reveal has been laid out.
      requestAnimationFrame(() => {
        const p = buildLayout(graph, new Set()).placed.get(char)
        if (!p) return
        const k = Math.max(0.75, Math.min(1.3, view.k))
        setView({ x: size.w / 2 - (p.x + p.w / 2) * k, y: size.h / 2 - p.y * k, k })
      })
    },
    [graph, layout.placed, size.h, size.w, view.k],
  )

  useEffect(() => {
    if (focus) revealAndCentre(focus)
    // revealAndCentre changes with the view; only re-run when the focus target does
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus])

  // ------------------------------------------------------------------ gestures
  const drag = useRef<{ id: number; x: number; y: number; vx: number; vy: number; moved: boolean } | null>(null)
  const pinch = useRef<Map<number, { x: number; y: number }>>(new Map())
  const pinchStart = useRef<{ dist: number; k: number; cx: number; cy: number } | null>(null)

  const onPointerDown = (e: React.PointerEvent) => {
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    pinch.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pinch.current.size === 2) {
      const [a, b] = [...pinch.current.values()]
      pinchStart.current = {
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        k: view.k,
        cx: (a.x + b.x) / 2,
        cy: (a.y + b.y) / 2,
      }
      drag.current = null
      return
    }
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, vx: view.x, vy: view.y, moved: false }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (pinch.current.has(e.pointerId)) {
      pinch.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    }
    if (pinch.current.size === 2 && pinchStart.current) {
      const [a, b] = [...pinch.current.values()]
      const dist = Math.hypot(a.x - b.x, a.y - b.y)
      const ratio = dist / (pinchStart.current.dist || 1)
      const k = Math.min(MAX_SCALE, Math.max(MIN_SCALE, pinchStart.current.k * ratio))
      setView((v) => zoomAbout(v, k, pinchStart.current!.cx, pinchStart.current!.cy, hostRef.current))
      return
    }
    const d = drag.current
    if (!d || d.id !== e.pointerId) return
    const dx = e.clientX - d.x
    const dy = e.clientY - d.y
    if (!d.moved && Math.hypot(dx, dy) > 4) d.moved = true
    if (d.moved) setView((v) => ({ ...v, x: d.vx + dx, y: d.vy + dy }))
  }

  const onPointerUp = (e: React.PointerEvent) => {
    pinch.current.delete(e.pointerId)
    if (pinch.current.size < 2) pinchStart.current = null
    if (drag.current?.id === e.pointerId) drag.current = null
  }

  /** Keep the point under the cursor fixed while scaling. */
  function zoomAbout(v: View, k: number, clientX: number, clientY: number, host: HTMLElement | null): View {
    const rect = host?.getBoundingClientRect()
    const px = clientX - (rect?.left ?? 0)
    const py = clientY - (rect?.top ?? 0)
    const wx = (px - v.x) / v.k
    const wy = (py - v.y) / v.k
    return { k, x: px - wx * k, y: py - wy * k }
  }

  // Wheel must be a non-passive native listener, otherwise the browser scrolls the
  // page instead of letting us zoom.
  useEffect(() => {
    const el = hostRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      setView((v) => {
        const factor = Math.exp(-e.deltaY * 0.0016)
        const k = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.k * factor))
        return zoomAbout(v, k, e.clientX, e.clientY, el)
      })
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  /** Zoom out until the whole graph is on screen. Bound to the ⤢ button. */
  const fit = useCallback(() => {
    const k = Math.min(
      MAX_SCALE,
      Math.max(MIN_SCALE, Math.min((size.w - 24) / layout.width, (size.h - 24) / layout.height)),
    )
    setView({ k, x: 12, y: (size.h - layout.height * k) / 2 })
  }, [layout.height, layout.width, size.h, size.w])

  /**
   * The opening view: readable zoom, anchored on the first row.
   *
   * Fitting the whole graph instead would be useless on the larger levels - N1's
   * 1,833 nodes shrink to roughly 15% scale, where no glyph is legible. Starting at
   * 1:1 on the top-left of the tree means the first branch is readable immediately
   * and the learner scrolls down from a known starting point.
   */
  const frameTop = useCallback(() => {
    const k = DEFAULT_SCALE
    const topY = layout.visible.length
      ? Math.min(...layout.visible.map((p) => p.y)) - NODE_H / 2
      : 0
    const leftX = layout.visible.length ? Math.min(...layout.visible.map((p) => p.x)) : 0
    // Leave room on the left for the disclosure arrows, which sit outside the pill.
    setView({ k, x: TOP_MARGIN_X - (leftX - 20) * k, y: TOP_MARGIN_Y - topY * k })
  }, [layout.visible])

  // Frame once per graph, so switching level lands at the top of the new tree
  // without resetting the view while the learner is panning around.
  const fitKey = useRef('')
  useEffect(() => {
    if (fitKey.current === viewKey) return
    fitKey.current = viewKey
    frameTop()
    // deliberately not depending on frameTop: re-running on every layout change
    // would yank the viewport back whenever a branch is opened
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewKey])

  const expandAll = () => setCollapsed(new Set())
  const collapseAll = () => setCollapsed(new Set(graph.edges.map((e) => e.target)))

  const hoveredNode = hovered ? (layout.placed.get(hovered)?.node ?? null) : null
  useEffect(() => {
    onHover?.(hoveredNode)
    // onHover identity is not stable in callers; the node is what matters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredNode])

  return (
    <div ref={hostRef} className="dot-grid map-surface relative h-full w-full overflow-hidden rounded-2xl border border-ink-700/70">
      <svg
        width={size.w}
        height={size.h}
        className="block cursor-grab active:cursor-grabbing"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="tree"
        aria-label="Kanji decomposition map"
      >
        <g transform={`translate(${view.x},${view.y}) scale(${view.k})`}>
          {/* secondary links first so primary edges sit on top */}
          {layout.links
            .filter((l) => !l.primary)
            .map((l) => (
              <path
                key={`s-${l.from.node.char}-${l.to.node.char}`}
                d={linkPath(l.from, l.to)}
                fill="none"
                stroke="#3a4354"
                strokeWidth={1}
                strokeDasharray="3 4"
                opacity={0.5}
              />
            ))}
          {layout.links
            .filter((l) => l.primary)
            .map((l) => (
              <path
                key={`p-${l.from.node.char}-${l.to.node.char}`}
                d={linkPath(l.from, l.to)}
                fill="none"
                stroke={
                  hovered === l.from.node.char || hovered === l.to.node.char ? '#64748b' : '#39414f'
                }
                strokeWidth={1.4}
              />
            ))}

          {layout.visible.map((p) => (
            <NodePill
              key={p.node.char}
              p={p}
              selected={selected === p.node.char}
              hovered={hovered === p.node.char}
              dim={dimmed.has(p.node.char)}
              collapsed={collapsed.has(p.node.char)}
              busy={loadingFamily.has(p.node.char)}
              onSelect={() => {
                if (!drag.current?.moved) onSelect(p.node.char)
              }}
              onToggle={() => toggle(p.node.char)}
              onEnter={() => setHovered(p.node.char)}
              onLeave={() => setHovered((h) => (h === p.node.char ? null : h))}
            />
          ))}
        </g>
      </svg>

      {/* controls */}
      <div className="absolute right-3 top-3 flex flex-col gap-1.5">
        <MapButton label="Zoom in" onClick={() => setView((v) => ({ ...v, k: Math.min(MAX_SCALE, v.k * 1.25) }))}>
          +
        </MapButton>
        <MapButton label="Zoom out" onClick={() => setView((v) => ({ ...v, k: Math.max(MIN_SCALE, v.k / 1.25) }))}>
          −
        </MapButton>
        <MapButton label="Back to the top of the graph" onClick={frameTop}>
          ⤒
        </MapButton>
        <MapButton label="Fit whole graph on screen" onClick={fit}>
          ⤢
        </MapButton>
      </div>
      <div className="absolute left-3 top-3 flex gap-1.5">
        <MapButton label="Expand every branch" onClick={expandAll} wide>
          Expand all
        </MapButton>
        <MapButton label="Collapse every branch" onClick={collapseAll} wide>
          Collapse all
        </MapButton>
      </div>

      {/* Hover readout: meaning + romaji, the thing the map exists to teach.
          Hidden below lg on two counts - it would sit under the Filters button in
          that corner, and there is no hover on a touch screen: a tap opens the
          detail sheet, which shows this and more. */}
      {hoveredNode && (
        <div className="pointer-events-none absolute bottom-3 left-3 hidden animate-fade-up rounded-xl border border-ink-700 bg-ink-950/95 px-3 py-2 shadow-panel lg:block">
          <div className="flex items-center gap-2">
            <span className="jp text-2xl leading-none text-slate-100">{hoveredNode.char}</span>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-slate-100">{hoveredNode.meaning || '—'}</div>
              <div className="text-[11px] text-ink-500">
                {hoveredNode.romaji || '—'}
                {hoveredNode.strokes ? ` · ${hoveredNode.strokes} strokes` : ''}
                {hoveredNode.parent_count > 0 ? ` · used in ${hoveredNode.parent_count}` : ''}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="pointer-events-none absolute bottom-3 right-3 rounded-lg bg-ink-950/80 px-2 py-1 text-[10px] text-ink-500">
        {layout.visible.length} of {graph.nodes.length} shown · {Math.round(view.k * 100)}%
      </div>
    </div>
  )
}

function MapButton({
  children,
  onClick,
  label,
  wide,
}: {
  children: React.ReactNode
  onClick: () => void
  label: string
  wide?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      // 44px on touch, 32px with a mouse: a pointer hits a 32px square reliably,
      // a thumb does not.
      className={`rounded-lg border border-ink-700 bg-ink-900/90 text-xs font-semibold text-slate-300
                  transition active:bg-ink-800 hover:border-ink-600 hover:text-white
                  ${wide ? 'min-h-[44px] px-3 lg:min-h-0 lg:px-2.5 lg:py-1.5' : 'h-11 w-11 lg:h-8 lg:w-8'}`}
    >
      {children}
    </button>
  )
}

/** Elbow connector: out of the parent's right edge, into the child's left edge. */
function linkPath(from: Placed, to: Placed): string {
  const x1 = from.x + from.w
  const y1 = from.y
  const x2 = to.x
  const y2 = to.y
  const mid = x1 + Math.max(14, (x2 - x1) / 2)
  return `M${x1},${y1} H${mid} V${y2} H${x2}`
}

const statusFill: Record<Status, string> = {
  learning: '#f59e0b',
  review: '#3b82f6',
  mastered: '#22c55e',
}

function NodePill({
  p,
  selected,
  hovered,
  dim,
  collapsed,
  busy,
  onSelect,
  onToggle,
  onEnter,
  onLeave,
}: {
  p: Placed
  selected: boolean
  hovered: boolean
  dim: boolean
  collapsed: boolean
  busy: boolean
  onSelect: () => void
  onToggle: () => void
  onEnter: () => void
  onLeave: () => void
}) {
  const { node } = p
  const accent = node.level ? levelColor[node.level as Level] : '#55607a'
  const y = p.y - NODE_H / 2
  // Dimmed nodes stay fully interactive - they are the point of the cross-level
  // expansion - so this is opacity only, not pointer-events.
  const faded = dim && !selected && !hovered
  // A node with a loadable-but-unloaded family reads as closed, not open.
  const open = p.hasKids && !collapsed

  return (
    <g
      transform={`translate(${p.x},${y})`}
      onPointerEnter={onEnter}
      onPointerLeave={onLeave}
      style={{ cursor: 'pointer', opacity: faded ? 0.42 : 1 }}
    >
      {/* Disclosure arrow, mirroring the reference UI's red triangle. It also shows on
          nodes with nothing loaded yet but a known family waiting (moreAvailable), so
          an off-level character merged into the map can still be opened. */}
      {(p.hasKids || p.moreAvailable) && (
        <g
          onClick={(e) => {
            e.stopPropagation()
            onToggle()
          }}
          role="button"
          aria-label={open ? `Collapse ${node.char}` : `Expand ${node.char}`}
          aria-expanded={open}
        >
          {/* Invisible hit area. The drawn circle is 14 units across, which is a
              fingertip-sized miss on a phone even though it is a comfortable
              mouse target. This rect widens it to 30x40 without changing the
              look. It reaches slightly under the pill, but the pill's own rect
              is painted after this group and therefore wins the pointer where
              the two overlap - so tapping the glyph still selects the node. */}
          <rect x={-30} y={-5} width={32} height={NODE_H + 10} fill="transparent" />
          <circle cx={-9} cy={NODE_H / 2} r={7} fill="#12151b" stroke="#3a4354" strokeWidth={0.8} />
          {busy ? (
            <circle cx={-9} cy={NODE_H / 2} r={3} fill="none" stroke="#f43f5e" strokeWidth={1.6}
                    strokeDasharray="3 3">
              <animateTransform attributeName="transform" type="rotate" from={`0 -9 ${NODE_H / 2}`}
                                to={`360 -9 ${NODE_H / 2}`} dur="0.8s" repeatCount="indefinite" />
            </circle>
          ) : (
            <path
              d={open ? 'M-13,12.5 L-5,12.5 L-9,18 Z' : 'M-11.5,11 L-6,15 L-11.5,19 Z'}
              fill="#f43f5e"
            />
          )}
        </g>
      )}

      <rect
        width={p.w}
        height={NODE_H}
        rx={9}
        fill={selected ? `${accent}2e` : hovered ? '#232a36' : dim ? '#161a22' : '#1b2029'}
        stroke={selected ? accent : hovered ? '#4b5568' : '#333c4b'}
        strokeWidth={selected ? 1.8 : 1}
        // dashed outline marks "outside the current level filter" for anyone who
        // cannot rely on the opacity difference alone
        strokeDasharray={dim && !selected ? '4 3' : undefined}
        onClick={onSelect}
      />

      <text
        x={11}
        y={NODE_H / 2 + 6}
        className="jp"
        fontSize={16}
        fill={selected ? '#fff' : '#e2e8f0'}
        onClick={onSelect}
      >
        {node.char}
      </text>

      {/* level chip */}
      {node.level && (
        <g transform={`translate(${33},${NODE_H / 2 - 7})`}>
          <rect width={26} height={14} rx={7} fill={`${accent}26`} />
          <text x={13} y={10.5} fontSize={8.5} fontWeight={700} fill={accent} textAnchor="middle">
            {node.level}
          </text>
        </g>
      )}

      {/* role chip: R / RK / C */}
      <g transform={`translate(${node.level ? 62 : 33},${NODE_H / 2 - 7})`}>
        <rect width={21} height={14} rx={7} fill="#2a3140" />
        <text x={10.5} y={10.5} fontSize={8} fontWeight={700} fill="#8a94a8" textAnchor="middle">
          {node.is_radical && node.is_kanji ? 'RK' : node.is_radical ? 'R' : 'C'}
        </text>
      </g>

      {/* mastery tick */}
      {node.status && (
        <g transform={`translate(${p.w - 17},${NODE_H / 2 - 7})`}>
          <circle cx={7} cy={7} r={7} fill={statusFill[node.status]} opacity={node.status === 'mastered' ? 1 : 0.85} />
          {node.status === 'mastered' && (
            <path d="M3.6,7.2 L5.9,9.5 L10.4,4.9" fill="none" stroke="#0d0f13" strokeWidth={1.6} strokeLinecap="round" />
          )}
        </g>
      )}
    </g>
  )
}
